import { useI18n } from "@core/i18n/i18n";
import { useNavigate } from "@core/routing/use-navigate";
import { analyticsService, notificationBadgeManager, notificationManager } from "@core/service-container";
import { AnalyticsCategory } from "@domain/analytics/analytics-types";
import { Notification } from "@domain/notification/notification";
import { useCallback } from "react";

type NotificationLocation = "notification-center" | "toast";

interface useNotificationHook {
	formatNotification: (notification: Notification) => string;
	onNotificationClicked: (notification: Notification, location: NotificationLocation) => Promise<void>;
}

export const useNotification = (): useNotificationHook => {
	const navigate = useNavigate();
	const i18n = useI18n();

	const formatNotification = useCallback((notification: Notification) => notification.format(i18n), [i18n]);

	const onNotificationClicked = useCallback(
		async (notification: Notification, location: NotificationLocation) => {
			if (!notification.isRead) {
				await notificationManager.markAsRead(notification.id);
				notificationBadgeManager.saveLastUnreadCount();
			}
			notification.onClick(navigate);

			analyticsService.trackEvent({
				category: AnalyticsCategory.NOTIFICATIONS,
				action: location === "notification-center" ? "clickNotification" : "clickNotificationToast",
				params: [notification.type],
			});
		},
		[navigate],
	);

	return { onNotificationClicked, formatNotification };
};
